import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { User } from 'lucide-react';
import { format } from 'date-fns';

interface ContactPreviewCardProps {
  contactName: string;
  phone: string;
  relationshipType: string;
  birthday?: Date;
  relationshipData?: Record<string, any>;
  avatar?: string;
}

export function ContactPreviewCard({
  contactName,
  phone,
  relationshipType,
  birthday,
  relationshipData,
  avatar,
}: ContactPreviewCardProps) {
  // Extract first name from full name
  const firstName = contactName ? contactName.split(' ')[0] : 'Contact';

  // Helper function to format field names
  const formatLabel = (fieldName: string) => {
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to format field values
  const formatValue = (value: any) => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (!value) return '-';

    // Try to parse as date
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (typeof value === 'string' && dateRegex.test(value)) {
      try {
        return format(new Date(value), 'MMM dd, yyyy');
      } catch {
        return value;
      }
    }

    return value;
  };

  return (
    <div className="bg-white rounded-lg p-6 w-[380px]">
      <h4 className="text-[20px] font-bold text-wabisabi-plum mb-6">
        {firstName}'s contact card
      </h4>

      {/* Avatar and Name */}
      <div className="flex flex-col items-center mb-6">
        <Avatar className="w-20 h-20 mb-4">
          {avatar ? (
            <AvatarImage src={`/images/avatars/${avatar}`} alt={contactName || 'Contact'} />
          ) : null}
          <AvatarFallback className="bg-gray-200">
            <User className="w-10 h-10 text-gray-500" />
          </AvatarFallback>
        </Avatar>
        <p className="text-[16px] font-bold text-black">
          {contactName || 'No name yet'}
        </p>
      </div>

      {/* Contact Details */}
      <div>
        {/* Phone */}
        <div className="flex justify-between items-start pb-4 border-b border-gray-200">
          <span className="text-[14px] text-[#8E8E8E]">Phone</span>
          <span className="text-[14px] text-black text-right">
            {phone || '-'}
          </span>
        </div>

        {/* Relationship */}
        <div className="flex justify-between items-start py-4 border-b border-gray-200">
          <span className="text-[14px] text-[#8E8E8E]">Relationship</span>
          <span className="text-[14px] text-black text-right capitalize">
            {relationshipType || '-'}
          </span>
        </div>

        {/* Birthday */}
        <div className="flex justify-between items-start py-4 border-b border-gray-200">
          <span className="text-[14px] text-[#8E8E8E]">Birthday</span>
          <span className="text-[14px] text-black text-right">
            {birthday ? format(birthday, 'MMM dd, yyyy') : '-'}
          </span>
        </div>

        {/* Dynamic relationship fields */}
        {relationshipData && Object.entries(relationshipData).map(([key, value]) => {
          // Skip empty values
          if (!value && value !== false) return null;

          return (
            <div key={key} className="flex justify-between items-start py-4 border-b border-gray-200">
              <span className="text-[14px] text-[#8E8E8E]">{formatLabel(key)}</span>
              <span className="text-[14px] text-black text-right max-w-[60%]">
                {formatValue(value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
